/**
 * Case Study Utility Functions
 *
 * Handles loading, filtering, sorting, and searching case studies
 * Currently loads from JSON files, ready for future database migration
 */

// Import all case study JSON files
// In future, this will be replaced with API calls to Supabase
import emailAutomation from "@/data/case-studies/email-automation-saas.json";
import governmentScheduling from "@/data/case-studies/government-scheduling-platform.json";
import manufacturingERP from "@/data/case-studies/manufacturing-erp-system.json";
import medicalDocumentation from "@/data/case-studies/medical-documentation-automation.json";
import payrollAutomation from "@/data/case-studies/payroll-automation-system.json";
import tileSlabERP from "@/data/case-studies/tile-slab-installation-erp.json";
import type {
  CaseStudy,
  CaseStudyFilters,
  CaseStudySortOptions,
} from "@/types/case-study.types";

const ALL_CASE_STUDIES: CaseStudy[] = [
  emailAutomation as CaseStudy,
  medicalDocumentation as CaseStudy,
  manufacturingERP as CaseStudy,
  payrollAutomation as CaseStudy,
  governmentScheduling as CaseStudy,
  tileSlabERP as CaseStudy,
];

/**
 * Get all case studies
 * Future: Replace with API call to `/api/case-studies`
 */
export function getAllCaseStudies(): CaseStudy[] {
  return ALL_CASE_STUDIES;
}

/**
 * Get case study by slug
 * Future: Replace with API call to `/api/case-studies/${slug}`
 */
export function getCaseStudyBySlug(slug: string): CaseStudy | undefined {
  return ALL_CASE_STUDIES.find((cs) => cs.metadata.slug === slug);
}

/**
 * Get case study by ID
 * Future: Replace with API call to `/api/case-studies/${id}`
 */
export function getCaseStudyById(id: string): CaseStudy | undefined {
  return ALL_CASE_STUDIES.find((cs) => cs.metadata.id === id);
}

/**
 * Get featured case studies (for homepage)
 */
export function getFeaturedCaseStudies(): CaseStudy[] {
  return ALL_CASE_STUDIES.filter(
    (cs) => cs.metadata.featured && cs.metadata.status === "published",
  );
}

/**
 * Get published case studies only
 */
export function getPublishedCaseStudies(): CaseStudy[] {
  return ALL_CASE_STUDIES.filter((cs) => cs.metadata.status === "published");
}

/**
 * Filter case studies based on criteria
 */
export function filterCaseStudies(
  caseStudies: CaseStudy[],
  filters: CaseStudyFilters,
): CaseStudy[] {
  let filtered = [...caseStudies];

  // Filter by services
  if (filters.services && filters.services.length > 0) {
    filtered = filtered.filter((cs) =>
      filters.services!.some((service) =>
        cs.taxonomy.services.includes(service),
      ),
    );
  }

  // Filter by industries
  if (filters.industries && filters.industries.length > 0) {
    filtered = filtered.filter((cs) =>
      filters.industries!.some((industry) =>
        cs.taxonomy.industries.includes(industry),
      ),
    );
  }

  // Filter by technologies
  if (filters.technologies && filters.technologies.length > 0) {
    filtered = filtered.filter((cs) =>
      filters.technologies!.some((tech) =>
        cs.taxonomy.technologies.includes(tech),
      ),
    );
  }

  // Filter by project types
  if (filters.projectTypes && filters.projectTypes.length > 0) {
    filtered = filtered.filter((cs) =>
      filters.projectTypes!.some((type) =>
        cs.taxonomy.projectTypes.includes(type),
      ),
    );
  }

  // Filter by company size
  if (filters.companySize && filters.companySize.length > 0) {
    filtered = filtered.filter((cs) =>
      filters.companySize!.includes(cs.client.companySizeCategory),
    );
  }

  // Search term filtering
  if (filters.searchTerm?.trim()) {
    const searchLower = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(
      (cs) =>
        cs.overview.title.toLowerCase().includes(searchLower) ||
        cs.overview.summary.toLowerCase().includes(searchLower) ||
        cs.overview.tagline.toLowerCase().includes(searchLower) ||
        cs.client.industryType.toLowerCase().includes(searchLower) ||
        cs.taxonomy.services.some((s) =>
          s.toLowerCase().includes(searchLower),
        ) ||
        cs.taxonomy.industries.some((i) =>
          i.toLowerCase().includes(searchLower),
        ) ||
        cs.taxonomy.technologies.some((t) =>
          t.toLowerCase().includes(searchLower),
        ),
    );
  }

  return filtered;
}

/**
 * Sort case studies
 */
export function sortCaseStudies(
  caseStudies: CaseStudy[],
  sortOptions: CaseStudySortOptions,
): CaseStudy[] {
  const sorted = [...caseStudies];

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (sortOptions.field) {
      case "publicationDate":
        comparison =
          new Date(a.metadata.publicationDate).getTime() -
          new Date(b.metadata.publicationDate).getTime();
        break;
      case "title":
        comparison = a.overview.title.localeCompare(b.overview.title);
        break;
      case "duration":
        const aDays =
          a.overview.duration.unit === "weeks"
            ? a.overview.duration.value * 7
            : a.overview.duration.value * 30;
        const bDays =
          b.overview.duration.unit === "weeks"
            ? b.overview.duration.value * 7
            : b.overview.duration.value * 30;
        comparison = aDays - bDays;
        break;
    }

    return sortOptions.direction === "desc" ? -comparison : comparison;
  });

  return sorted;
}

/**
 * Get all unique values for filter dropdowns
 */
export function getFilterOptions() {
  const services = new Set<string>();
  const industries = new Set<string>();
  const technologies = new Set<string>();
  const projectTypes = new Set<string>();
  const companySizes = new Set<CaseStudy["client"]["companySizeCategory"]>();

  ALL_CASE_STUDIES.forEach((cs) => {
    cs.taxonomy.services.forEach((s) => services.add(s));
    cs.taxonomy.industries.forEach((i) => industries.add(i));
    cs.taxonomy.technologies.forEach((t) => technologies.add(t));
    cs.taxonomy.projectTypes.forEach((pt) => projectTypes.add(pt));
    companySizes.add(cs.client.companySizeCategory);
  });

  return {
    services: Array.from(services).sort(),
    industries: Array.from(industries).sort(),
    technologies: Array.from(technologies).sort(),
    projectTypes: Array.from(projectTypes).sort(),
    companySizes: Array.from(companySizes).sort(),
  };
}

/**
 * Get related case studies
 */
export function getRelatedCaseStudies(
  caseStudyId: string,
  limit = 3,
): CaseStudy[] {
  const caseStudy = getCaseStudyById(caseStudyId);
  if (!caseStudy?.relatedCaseStudies) {
    return [];
  }

  return caseStudy.relatedCaseStudies
    .map((id) => getCaseStudyById(id))
    .filter((cs): cs is CaseStudy => cs !== undefined)
    .slice(0, limit);
}

/**
 * Format duration for display
 */
export function formatDuration(
  duration: CaseStudy["overview"]["duration"],
): string {
  return `${duration.value} ${duration.unit}`;
}

/**
 * Calculate reading time estimate (rough)
 */
export function estimateReadingTime(caseStudy: CaseStudy): number {
  const wordsPerMinute = 200;
  const totalWords = caseStudy.sections.reduce((total, section) => {
    return total + section.content.split(/\s+/).length;
  }, 0);
  return Math.ceil(totalWords / wordsPerMinute);
}

/**
 * Get case study meta tags for SEO
 */
export function getCaseStudyMetaTags(caseStudy: CaseStudy) {
  return {
    title: caseStudy.seo.metaTitle,
    description: caseStudy.seo.metaDescription,
    keywords: caseStudy.seo.keywords.join(", "),
    ogImage: caseStudy.seo.ogImage || caseStudy.media.heroImage.url,
    ogType: "article" as const,
    ogUrl: `/case-studies/${caseStudy.metadata.slug}`,
    publishedTime: caseStudy.metadata.publicationDate,
    modifiedTime: caseStudy.metadata.lastUpdated,
  };
}
